/**
 * Feature Flags Editor Component
 *
 * Tree-based toggle editor for all GUS configuration:
 * - 8 Modules (Social Atlas, Assessment, Planning, etc.)
 * - Backend Feature Flags (11 flags)
 * - Frontend Feature Flags (13 flags)
 * - Settings Tabs (8 tabs)
 * - Theme Configuration
 * - Localization Settings
 * - Data Sources (9 sources)
 * - Map Styles (5 styles)
 */

import React, { useState, useCallback, useMemo, useEffect } from 'react';
import styled from 'styled-components';
import { Alert, Button as MuiButton, CircularProgress, Collapse } from '@mui/material';
import {
  ExpandMore,
  ExpandLess,
  Warning as WarningIcon,
  Save,
  Undo,
} from '@mui/icons-material';
import { Toggle } from '../design-system/components/Toggle';
import { Card } from '../design-system/components/Card';
import { spacing, typography, transitions, borderRadius } from '../design-system/tokens';
import { projectAPI, FlagManifestResponse } from '../services/api';
import FlagTree, { Decorate } from './flags/FlagTree';
import { satisfactionDecorate } from './satisfaction/satisfactionDecorations';
import LeadershipBriefPanel from './executive/LeadershipBriefPanel';
import { executiveDecorate, manifestSaysExecutiveOn } from './executive/executiveDecorations';
import { setPath } from './flags/flagPaths';

// =============================================================================
// TYPES
// =============================================================================

// User roles for RBAC
export type UserRole = 'admin' | 'analyst' | 'viewer' | 'editor';

export const USER_ROLES: { id: UserRole; label: string; description: string }[] = [
  { id: 'admin', label: 'Administrator', description: 'Full access to all features' },
  { id: 'analyst', label: 'Analyst', description: 'Can view and analyze data, create reports' },
  { id: 'viewer', label: 'Viewer', description: 'Read-only access to dashboards and reports' },
  { id: 'editor', label: 'Editor', description: 'Can upload data and create maps' },
];

// Subtab/feature definition
interface SubItem {
  id: string;
  label: string;
  label_ar?: string;
  roles?: UserRole[];
}

// Module tab definition with subtabs and roles
interface ModuleTab {
  id: string;
  label: string;
  label_ar?: string;
  tab_id?: string;  // Maps to GUS internal tab ID
  // What GUS does when the tab's key is absent. Every tab is on unless it says
  // otherwise; the Leadership Brief is off until an admin turns it on (GUS's
  // rule from its Leadership Brief release; older builds read absent as on).
  defaultEnabled?: boolean;
  roles?: UserRole[];
  subtabs?: SubItem[];
  stages?: SubItem[];  // For report generator
  report_types?: SubItem[];  // For report generator
}

// Module tool definition (for Map workspace)
interface ModuleTool {
  id: string;
  label: string;
  label_ar?: string;
  roles?: UserRole[];
}

// Module feature definition (for Data Management, Maps Library)
interface ModuleFeature {
  id: string;
  label: string;
  label_ar?: string;
  roles?: UserRole[];
  allowed_types?: { id: string; label: string }[];  // For file upload
}

// Module widget definition (for Dashboard)
interface ModuleWidget {
  id: string;
  label: string;
  label_ar?: string;
  roles?: UserRole[];
}

// Export format definition
interface ExportFormat {
  id: string;
  label: string;
}

// Module definition matching GUS schema with RBAC
interface ModuleDefinition {
  id: string;
  label: string;
  label_ar?: string;
  description: string;
  roles?: UserRole[];  // Default roles for the module
  tabs?: ModuleTab[];  // For Atlas, Reports, Assessments
  // Config key the tabs are saved under. GUS reads GUS Chat's items at
  // `modules.gus_chat.submenu.*`; every other module uses `tabs`.
  tabsContainer?: 'tabs' | 'submenu';
  tools?: ModuleTool[];  // For Map workspace
  features?: ModuleFeature[];  // For Data Management, Maps Library
  widgets?: ModuleWidget[];  // For Dashboard
  export_formats?: ExportFormat[];  // For Map workspace
}

// Saved config for a module's tab-shaped items, under `tabs` or `submenu`.
interface TabConfigMap {
  [tabId: string]: {
    enabled: boolean;
    label?: string;
    label_ar?: string;
    roles?: UserRole[];
    subtabs?: { [subtabId: string]: { enabled: boolean; roles?: UserRole[] } };
    stages?: { [stageId: string]: { enabled: boolean } };
    report_types?: { [typeId: string]: { enabled: boolean } };
  };
}

interface FeatureFlagsConfig {
  modules: {
    [moduleId: string]: {
      enabled: boolean;
      label?: string;
      label_ar?: string;
      roles?: UserRole[];
      tabs?: TabConfigMap;
      submenu?: TabConfigMap;
      tools?: {
        [toolId: string]: {
          enabled: boolean;
          label?: string;
          label_ar?: string;
          roles?: UserRole[];
        };
      };
      features?: {
        [featureId: string]: {
          enabled: boolean;
          roles?: UserRole[];
        };
      };
      widgets?: {
        [widgetId: string]: {
          enabled: boolean;
          roles?: UserRole[];
        };
      };
      export_formats?: {
        [formatId: string]: {
          enabled: boolean;
        };
      };
    };
  };
}

interface BackendFeatureFlags {
  [key: string]: boolean;
}

interface FrontendFeatureFlags {
  [key: string]: boolean;
}

interface SettingsConfig {
  [tabId: string]: {
    enabled: boolean;
    label?: string;
  };
}

interface DataSourcesConfig {
  [sourceId: string]: {
    enabled: boolean;
    label?: string;
  };
}

interface MapStylesConfig {
  [styleId: string]: {
    enabled: boolean;
    label?: string;
    url?: string;
  };
}

interface GlobalFeaturesConfig {
  [featureId: string]: {
    enabled: boolean;
    roles?: UserRole[];
    options?: { [optionId: string]: { enabled: boolean; roles?: UserRole[] } };
    formats?: string[];
  };
}

interface LocalizationConfig {
  default_language: string;
  supported_languages: string[];
  date_format?: string;
  number_format?: string;
}

interface ThemeConfig {
  mode?: 'light' | 'dark' | 'system';
  primary_color?: string;
  accent_color?: string;
}

// Backend Configuration for GUS Chat and Security settings
export interface BackendConfig {
  document_only_mode: boolean;
  enable_llm_classification: boolean;
  enable_agent_dataset_tools: boolean;
  enable_prompt_guard: boolean;
  enable_quality_metrics: boolean;
  require_email_verification: boolean;
  session_timeout_hours: number;
}

export interface FeatureFlagsEditorProps {
  projectId?: string;
  featureFlags?: FeatureFlagsConfig;
  backendFeatureFlags?: BackendFeatureFlags;
  frontendFeatureFlags?: FrontendFeatureFlags;
  settingsConfig?: SettingsConfig;
  dataSourcesConfig?: DataSourcesConfig;
  mapStylesConfig?: MapStylesConfig;
  localizationConfig?: LocalizationConfig;
  themeConfig?: ThemeConfig;
  globalFeaturesConfig?: GlobalFeaturesConfig;
  backendConfig?: BackendConfig;
  onChange: (updates: {
    featureFlags?: FeatureFlagsConfig;
    backendFeatureFlags?: BackendFeatureFlags;
    frontendFeatureFlags?: FrontendFeatureFlags;
    settingsConfig?: SettingsConfig;
    dataSourcesConfig?: DataSourcesConfig;
    mapStylesConfig?: MapStylesConfig;
    localizationConfig?: LocalizationConfig;
    themeConfig?: ThemeConfig;
    globalFeaturesConfig?: GlobalFeaturesConfig;
    backendConfig?: BackendConfig;
  }) => void;
  onSave?: () => Promise<void>;
  onReset?: () => void;
  loading?: boolean;
  readOnly?: boolean;
}

// =============================================================================
// MODULE DEFINITIONS (matching GUS IconSidebar.tsx MODULE_CONFIG_MAP)
// =============================================================================

/**
 * Whether a stored tab entry is on. A tab flag is a boolean or an object with
 * `enabled`; anything else, absence included, reads as the tab's default.
 */
export const tabIsOn = (stored: unknown, defaultOn: boolean): boolean => {
  if (typeof stored === 'boolean') return stored;
  if (stored && typeof stored === 'object' && typeof (stored as { enabled?: unknown }).enabled === 'boolean') {
    return (stored as { enabled: boolean }).enabled;
  }
  return defaultOn;
};

// Exported so tests can hold the registry to the paths GUS actually reads.
export const MODULE_DEFINITIONS: ModuleDefinition[] = [
  {
    id: 'gus_chat',
    label: 'GUS Chat',
    label_ar: 'دردشة GUS',
    description: 'AI-powered natural language queries',
    roles: ['admin', 'analyst', 'viewer', 'editor'],
    // GUS gates these two sidebar items on `modules.gus_chat.submenu.*`
    // (moduleFlags.ts). Saved under `tabs` they did nothing.
    tabsContainer: 'submenu',
    tabs: [
      { id: 'chat_history', label: 'Chat History', roles: ['admin', 'analyst', 'viewer', 'editor'] },
      { id: 'saved_chats', label: 'Saved Chats', roles: ['admin', 'analyst', 'editor'] },
    ],
  },
  {
    id: 'atlas',
    label: 'Socio-Economic Atlas',
    label_ar: 'الأطلس الاجتماعي الاقتصادي',
    description: 'Urban metrics and demographic data visualization',
    roles: ['admin', 'analyst', 'viewer'],
    // 9 top-level Atlas tabs — canonical names matching GUS v2 contract (PR #405)
    // Only these 9 tabs.<tab>.enabled flags are enforced by isFeatureEnabled() today.
    // Sub-tab flags (72 additional paths) will be added when GUS v2 FE sweep lands.
    tabs: [
      {
        id: 'city_insights',
        label: 'City Insights',
        label_ar: 'رؤى المدينة',
        roles: ['admin', 'analyst', 'viewer'],
      },
      {
        id: 'satisfaction',
        label: 'Satisfaction',
        label_ar: 'رضا المدينة',
        roles: ['admin', 'analyst', 'viewer'],
      },
      {
        id: 'community',
        label: 'Community',
        label_ar: 'المجتمع',
        roles: ['admin', 'analyst', 'viewer'],
      },
      {
        id: 'urban_pulse',
        label: 'Urban Pulse',
        label_ar: 'نبض المدينة',
        roles: ['admin', 'analyst', 'viewer'],
      },
      {
        id: 'urban_intelligence',
        label: 'Urban Intelligence',
        label_ar: 'الذكاء الحضري',
        roles: ['admin', 'analyst'],
      },
      {
        id: 'spatial_economics',
        label: 'Spatial Economics',
        label_ar: 'الاقتصاد المكاني',
        roles: ['admin', 'analyst'],
      },
      {
        id: 'vulnerability',
        label: 'Vulnerability',
        label_ar: 'الهشاشة',
        roles: ['admin', 'analyst'],
      },
      {
        id: 'plan_assessment',
        label: 'Plan Assessment Studio',
        label_ar: 'استوديو تقييم الخطط',
        roles: ['admin', 'analyst'],
      },
      {
        // GUS reads `modules.atlas.tabs.executive`; only the label people see is
        // "Leadership Brief". Its settings are in the Leadership Brief section.
        id: 'executive',
        label: 'Leadership Brief',
        label_ar: 'الموجز القيادي',
        roles: ['admin', 'analyst', 'viewer'],
        defaultEnabled: false,
      },
    ],
  },
  {
    id: 'reports',
    label: 'Reports',
    label_ar: 'التقارير',
    description: 'Report generation and export',
    roles: ['admin', 'analyst'],
    tabs: [
      { id: 'chat_reports', label: 'Chat Reports', roles: ['admin', 'analyst'] },
      {
        id: 'report_generator',
        label: 'Report Generator',
        roles: ['admin', 'analyst'],
        stages: [
          { id: 'stage1_report_type', label: 'Report Type' },
          { id: 'stage2_detail_level', label: 'Detail Level' },
          { id: 'stage3_content_options', label: 'Content Options' },
          { id: 'stage4_expert_team', label: 'Expert Team' },
          { id: 'stage5_generate_refine', label: 'Generate & Refine' },
        ],
        report_types: [
          { id: 'urban_planning', label: 'Urban Planning Report' },
          { id: 'economic_analysis', label: 'Economic Analysis' },
          { id: 'social_impact', label: 'Social Impact Assessment' },
          { id: 'infrastructure', label: 'Infrastructure Assessment' },
          { id: 'environmental', label: 'Environmental Analysis' },
          { id: 'custom', label: 'Custom Report' },
        ],
      },
      { id: 'library', label: 'Library', roles: ['admin', 'analyst', 'viewer'] },
    ],
  },
  {
    id: 'map',
    label: 'Map Workspace',
    label_ar: 'مساحة عمل الخريطة',
    description: 'Interactive mapping and spatial analysis',
    roles: ['admin', 'analyst', 'editor', 'viewer'],
    tools: [
      { id: 'data_upload', label: 'Load Data', label_ar: 'تحميل البيانات', roles: ['admin', 'editor'] },
      { id: 'layers', label: 'Layers', label_ar: 'الطبقات', roles: ['admin', 'analyst', 'editor', 'viewer'] },
      { id: 'filters', label: 'Data Operations', label_ar: 'عمليات البيانات', roles: ['admin', 'analyst', 'editor'] },
      { id: 'charts', label: 'Charts', label_ar: 'الرسوم البيانية', roles: ['admin', 'analyst', 'editor', 'viewer'] },
      { id: 'view_3d', label: '3D Controls', label_ar: 'عناصر التحكم ثلاثية الأبعاد', roles: ['admin', 'analyst', 'editor', 'viewer'] },
      { id: 'spatial_analysis', label: 'Spatial Analysis', label_ar: 'التحليل المكاني', roles: ['admin', 'analyst'] },
      { id: 'export', label: 'Export', label_ar: 'تصدير', roles: ['admin', 'analyst', 'editor'] },
    ],
    export_formats: [
      { id: 'png', label: 'PNG Image' },
      { id: 'html', label: 'Interactive HTML' },
      { id: 'json', label: 'JSON Configuration' },
      { id: 'csv', label: 'CSV Data' },
      { id: 'geojson', label: 'GeoJSON' },
      { id: 'pdf', label: 'PDF Report' },
    ],
  },
  // This module is SQUID, the site suitability view. The ids are legacy names
  // and must stay: GUS reads `modules.assessments` (sidebar icon) and
  // `modules.assessments.tabs.site_selection` (the SQUID view itself) by
  // dot-path, and nothing else under this module (issue #27). The site
  // selection, development assessment and transport tabs users see inside
  // Tools are gated under `modules.tools.tabs.*`, below.
  {
    id: 'assessments',
    label: 'SQUID',
    label_ar: 'SQUID',
    description: 'Site suitability (GUS SQUID). Module toggle controls the sidebar icon, the tab toggle the view',
    roles: ['admin', 'analyst'],
    tabs: [
      {
        id: 'site_selection',
        label: 'SQUID view',
        label_ar: 'عرض SQUID',
        roles: ['admin', 'analyst'],
      },
    ],
  },
  {
    id: 'data_management',
    label: 'Data Management',
    label_ar: 'إدارة البيانات',
    description: 'Dataset upload and management',
    roles: ['admin', 'editor'],
    features: [
      { id: 'document_upload', label: 'Document Upload', label_ar: 'تحميل المستندات', roles: ['admin', 'editor'] },
      { id: 'document_delete', label: 'Document Delete', label_ar: 'حذف المستندات', roles: ['admin'] },
      { id: 'file_upload', label: 'File Upload', roles: ['admin', 'editor'] },
      { id: 'database_import', label: 'Database Import', roles: ['admin'] },
      { id: 'url_import', label: 'URL Import', roles: ['admin', 'editor'] },
      { id: 'session_restore', label: 'Session Restore', roles: ['admin', 'editor'] },
      { id: 'dataset_explorer', label: 'Dataset Explorer', roles: ['admin', 'editor'] },
      { id: 'dataset_cleanup', label: 'Dataset Cleanup', roles: ['admin'] },
      { id: 'calculate_field', label: 'Calculate Field', roles: ['admin', 'editor'] },
      { id: 'spatial_join', label: 'Spatial Join', roles: ['admin'] },
      { id: 'geometry_operations', label: 'Geometry Operations', roles: ['admin'] },
      { id: 'h3_data_reload', label: 'H3 Data Reload', roles: ['admin'] },
      { id: 'force_database_save', label: 'Force Database Save', roles: ['admin'] },
      { id: 'force_data_reload', label: 'Force Data Reload', roles: ['admin'] },
    ],
  },
  {
    id: 'fieldnotes',
    label: 'Fieldnotes',
    label_ar: 'ملاحظات ميدانية',
    description: 'AI-powered fieldnotes combining maps, data, and narrative',
    roles: ['admin', 'analyst', 'viewer', 'editor'],
    features: [
      { id: 'create_fieldnote', label: 'Create Fieldnote', roles: ['admin', 'analyst', 'editor'] },
      { id: 'ai_builder', label: 'AI Builder', roles: ['admin', 'analyst'] },
      { id: 'share_fieldnote', label: 'Share Fieldnote', roles: ['admin', 'analyst'] },
      { id: 'dataset_upload', label: 'Dataset Upload', roles: ['admin', 'editor'] },
    ],
  },
  // GUS gates the Tools view on `modules.tools` and each tab on
  // `modules.tools.tabs.<id>` (AssessmentsApp.tsx). It never read the old
  // `modules.tools.features.*` keys, so those toggles did nothing (issue #27).
  {
    id: 'tools',
    label: 'Tools',
    label_ar: 'أدوات',
    description: 'Site selection, development assessment, transport and plan assessment',
    roles: ['admin', 'analyst'],
    tabs: [
      {
        id: 'site_selection',
        label: 'Site Selection',
        label_ar: 'اختيار الموقع',
        roles: ['admin', 'analyst'],
      },
      {
        id: 'development_assessment',
        label: 'Development Assessment',
        label_ar: 'تقييم التطوير',
        roles: ['admin', 'analyst'],
      },
      {
        id: 'transport',
        label: 'Transport',
        label_ar: 'النقل',
        roles: ['admin', 'analyst'],
      },
      {
        // GUS shows this tab if ANY of three keys is enabled or absent:
        // modules.tools.tabs.plan_assessment (this one),
        // modules.atlas.tabs.plan_assessment (in the Atlas module above) and
        // modules.atlas.tabs.policy_intelligence, a legacy key this registry
        // does not write. Absent keys count as enabled, so for a tenant whose
        // saved config lacks policy_intelligence the tab cannot be hidden from
        // the portal at all. Fixing that means dropping the legacy fallback in
        // gus-v2 AssessmentsApp.tsx, not adding a toggle here.
        id: 'plan_assessment',
        label: 'Plan Assessment Studio',
        label_ar: 'استوديو تقييم الخطط',
        roles: ['admin', 'analyst'],
      },
    ],
  },
  {
    id: 'maps_library',
    label: 'Maps Library',
    label_ar: 'مكتبة الخرائط',
    description: 'Saved maps and configurations',
    roles: ['admin', 'analyst', 'editor'],
    features: [
      { id: 'save_map', label: 'Save Map', roles: ['admin', 'analyst', 'editor'] },
      { id: 'load_map', label: 'Load Map', roles: ['admin', 'analyst', 'editor', 'viewer'] },
      { id: 'share_map', label: 'Share Map', roles: ['admin', 'analyst'] },
      { id: 'delete_map', label: 'Delete Map', roles: ['admin'] },
    ],
  },
];

const BACKEND_FLAGS: { id: string; label: string; description: string }[] = [
  { id: 'enable_caching', label: 'Enable Caching', description: 'Cache API responses' },
  { id: 'enable_rate_limiting', label: 'Rate Limiting', description: 'Limit API requests per user' },
  { id: 'enable_audit_logging', label: 'Audit Logging', description: 'Log all user actions' },
  { id: 'enable_webhooks', label: 'Webhooks', description: 'Enable webhook notifications' },
  { id: 'enable_background_jobs', label: 'Background Jobs', description: 'Process jobs asynchronously' },
  { id: 'enable_ai_fallback', label: 'AI Fallback', description: 'Use fallback model if primary fails' },
  { id: 'enable_rag', label: 'RAG Pipeline', description: 'Retrieval-augmented generation' },
  { id: 'enable_reranking', label: 'Reranking', description: 'Rerank search results with Cohere' },
  { id: 'enable_streaming', label: 'Streaming', description: 'Stream AI responses' },
  { id: 'enable_debug_mode', label: 'Debug Mode', description: 'Enable verbose logging' },
  { id: 'enable_maintenance_mode', label: 'Maintenance Mode', description: 'Show maintenance page' },
];

const FRONTEND_FLAGS: { id: string; label: string; description: string }[] = [
  { id: 'show_beta_features', label: 'Beta Features', description: 'Show experimental features' },
  { id: 'enable_dark_mode', label: 'Dark Mode', description: 'Allow dark mode toggle' },
  { id: 'enable_rtl', label: 'RTL Support', description: 'Right-to-left language support' },
  { id: 'enable_tour', label: 'Onboarding Tour', description: 'Show guided tour for new users' },
  { id: 'enable_feedback', label: 'Feedback Button', description: 'Show feedback collection' },
  { id: 'enable_shortcuts', label: 'Keyboard Shortcuts', description: 'Enable keyboard navigation' },
  { id: 'enable_notifications', label: 'Notifications', description: 'Browser notifications' },
  { id: 'enable_offline_mode', label: 'Offline Mode', description: 'Service worker caching' },
  { id: 'enable_analytics', label: 'Analytics', description: 'Track user interactions' },
  { id: 'show_map_controls', label: 'Map Controls', description: 'Show advanced map controls' },
  { id: 'show_data_quality', label: 'Data Quality', description: 'Show data quality indicators' },
  { id: 'enable_export', label: 'Export', description: 'Enable data export options' },
  { id: 'enable_sharing', label: 'Sharing', description: 'Enable sharing features' },
];

// Global features that apply across all modules (GUS useProjectConfig.ts)
interface GlobalFeatureDefinition {
  id: string;
  label: string;
  description: string;
  roles?: UserRole[];
  hasOptions?: boolean;  // Has sub-options like sharing.options
  hasFormats?: boolean;  // Has format list like export_functionality.formats
}

const GLOBAL_FEATURES: GlobalFeatureDefinition[] = [
  { id: 'ai_insights', label: 'AI Insights', description: 'AI-powered insights across all modules', roles: ['admin', 'analyst', 'viewer'] },
  { id: 'natural_language_queries', label: 'Natural Language Queries', description: 'Query data using natural language', roles: ['admin', 'analyst', 'viewer'] },
  { id: 'export_functionality', label: 'Export Functionality', description: 'Export data and visualizations', roles: ['admin', 'analyst', 'editor'], hasFormats: true },
  { id: 'sharing', label: 'Sharing', description: 'Share content with team members', roles: ['admin', 'analyst'], hasOptions: true },
  { id: 'streaming', label: 'AI Response Streaming', description: 'Stream AI responses in real-time', roles: ['admin', 'analyst', 'viewer'] },
  { id: 'debug_mode', label: 'Debug Mode', description: 'Show debug information and API logs', roles: ['admin'] },
  { id: 'beta_features', label: 'Beta Features', description: 'Enable experimental features', roles: ['admin'] },
];

const SETTINGS_TABS: { id: string; label: string }[] = [
  { id: 'general', label: 'General' },
  { id: 'appearance', label: 'Appearance' },
  { id: 'language', label: 'Language' },
  { id: 'notifications', label: 'Notifications' },
  { id: 'privacy', label: 'Privacy' },
  { id: 'data', label: 'Data Management' },
  { id: 'integrations', label: 'Integrations' },
  { id: 'advanced', label: 'Advanced' },
];

const DATA_SOURCES: { id: string; label: string; table?: string }[] = [
  { id: 'satisfaction', label: 'Satisfaction Data', table: 'satisfaction_monthly_data' },
  { id: 'demographics', label: 'Demographics', table: 'demographics_hex' },
  { id: 'land_use', label: 'Land Use', table: 'land_use_data' },
  { id: 'real_estate', label: 'Real Estate', table: 'real_estate_transactions' },
  { id: 'poi', label: 'POI Data', table: 'places_data' },
  { id: 'activity_patterns', label: 'Activity Patterns', table: 'activity_patterns' },
  { id: 'transport_od', label: 'Transport OD Flows', table: 'transport_od_flows' },
  { id: 'transport_congestion', label: 'Transport Congestion', table: 'transport_congestion' },
];

const MAP_STYLES: { id: string; label: string }[] = [
  { id: 'light', label: 'Light' },
  { id: 'dark', label: 'Dark' },
  { id: 'satellite', label: 'Satellite' },
  { id: 'streets', label: 'Streets' },
  { id: 'terrain', label: 'Terrain' },
];

// =============================================================================
// STYLED COMPONENTS
// =============================================================================

const EditorContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${spacing['4']};
`;

const Section = styled(Card)`
  margin-bottom: ${spacing['3']};
`;

const SectionHeader = styled.div<{ $expanded: boolean }>`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: ${spacing['3']} ${spacing['4']};
  cursor: pointer;
  user-select: none;
  border-radius: ${borderRadius.md};
  transition: ${transitions.all};

  &:hover {
    background: ${props => props.theme?.colors?.background?.hover || 'rgba(255, 255, 255, 0.05)'};
  }
`;

const SectionTitle = styled.div`
  display: flex;
  align-items: center;
  gap: ${spacing['3']};
`;

const SectionTitleText = styled.h3`
  margin: 0;
  font-size: ${typography.fontSize.base};
  font-weight: ${typography.fontWeight.semibold};
  color: ${props => props.theme?.colors?.text?.primary || '#ffffff'};
`;

const SectionSubtitle = styled.span`
  font-size: ${typography.fontSize.xs};
  color: ${props => props.theme?.colors?.text?.secondary || '#94a3b8'};
`;

const SectionActions = styled.div`
  display: flex;
  align-items: center;
  gap: ${spacing['2']};
`;

const EnableAllButton = styled.button`
  padding: ${spacing['1']} ${spacing['2']};
  font-size: ${typography.fontSize.xs};
  font-weight: ${typography.fontWeight.medium};
  color: ${props => props.theme?.colors?.accent?.primary || '#32828B'};
  background: transparent;
  border: 1px solid ${props => props.theme?.colors?.accent?.primary || '#32828B'};
  border-radius: ${borderRadius.sm};
  cursor: pointer;
  transition: ${transitions.all};

  &:hover {
    background: ${props => `${props.theme?.colors?.accent?.primary || '#32828B'}20`};
  }
`;

const SectionContent = styled.div`
  padding: 0 ${spacing['4']} ${spacing['4']};
`;

const ModuleItem = styled.div`
  border: 1px solid ${props => props.theme?.colors?.border?.primary || 'rgba(255, 255, 255, 0.1)'};
  border-radius: ${borderRadius.md};
  margin-bottom: ${spacing['2']};
  overflow: hidden;

  &:last-child {
    margin-bottom: 0;
  }
`;

const ModuleHeader = styled.div<{ $enabled: boolean }>`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: ${spacing['3']};
  background: ${props =>
    props.$enabled
      ? `${props.theme?.colors?.accent?.primary || '#32828B'}10`
      : 'transparent'};
  cursor: pointer;
  transition: ${transitions.all};

  &:hover {
    background: ${props => props.theme?.colors?.background?.hover || 'rgba(255, 255, 255, 0.05)'};
  }
`;

const ModuleInfo = styled.div`
  flex: 1;
  min-width: 0;
`;

const ModuleName = styled.span`
  font-size: ${typography.fontSize.sm};
  font-weight: ${typography.fontWeight.medium};
  color: ${props => props.theme?.colors?.text?.primary || '#ffffff'};
`;

const ModuleDescription = styled.span`
  display: block;
  font-size: ${typography.fontSize.xs};
  color: ${props => props.theme?.colors?.text?.secondary || '#94a3b8'};
  margin-top: 2px;
`;

const TabsContainer = styled.div`
  padding: ${spacing['3']};
  padding-left: ${spacing['8']};
  background: ${props => props.theme?.colors?.background?.secondary || 'rgba(26, 26, 26, 0.5)'};
  border-top: 1px solid ${props => props.theme?.colors?.border?.primary || 'rgba(255, 255, 255, 0.1)'};
`;

const TabItem = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: ${spacing['2']} 0;

  &:not(:last-child) {
    border-bottom: 1px solid ${props => props.theme?.colors?.border?.secondary || 'rgba(255, 255, 255, 0.05)'};
  }
`;

const FlagItem = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  padding: ${spacing['3']} 0;

  &:not(:last-child) {
    border-bottom: 1px solid ${props => props.theme?.colors?.border?.secondary || 'rgba(255, 255, 255, 0.05)'};
  }
`;

const FlagInfo = styled.div`
  flex: 1;
  min-width: 0;
  padding-right: ${spacing['4']};
`;

const FlagName = styled.span`
  font-size: ${typography.fontSize.sm};
  font-weight: ${typography.fontWeight.medium};
  color: ${props => props.theme?.colors?.text?.primary || '#ffffff'};
`;

const FlagDescription = styled.span`
  display: block;
  font-size: ${typography.fontSize.xs};
  color: ${props => props.theme?.colors?.text?.secondary || '#94a3b8'};
  margin-top: 2px;
`;

const ActionBar = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: ${spacing['2']};
  padding: ${spacing['4']};
  background: ${props => props.theme?.colors?.background?.secondary || 'rgba(26, 26, 26, 0.5)'};
  border-top: 1px solid ${props => props.theme?.colors?.border?.primary || 'rgba(255, 255, 255, 0.1)'};
  border-radius: 0 0 ${borderRadius.lg} ${borderRadius.lg};
`;

const CountBadge = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 20px;
  height: 20px;
  padding: 0 ${spacing['1']};
  font-size: ${typography.fontSize.xs};
  font-weight: ${typography.fontWeight.medium};
  color: #ffffff;
  background: ${props => props.theme?.colors?.accent?.primary || '#32828B'};
  border-radius: ${borderRadius.full};
`;

// Role selector styles
const RolesContainer = styled.div`
  display: flex;
  align-items: center;
  gap: ${spacing['2']};
  margin-left: ${spacing['4']};
`;

const RoleCheckbox = styled.div<{ $checked: boolean; $disabled?: boolean }>`
  display: inline-flex;
  align-items: center;
  gap: ${spacing['1']};
  padding: ${spacing['1']} ${spacing['2']};
  font-size: ${typography.fontSize.xs};
  color: ${props => props.$checked
    ? props.theme?.colors?.text?.primary || '#ffffff'
    : props.theme?.colors?.text?.secondary || '#94a3b8'};
  background: ${props => props.$checked
    ? `${props.theme?.colors?.accent?.primary || '#32828B'}20`
    : 'transparent'};
  border: 1px solid ${props => props.$checked
    ? props.theme?.colors?.accent?.primary || '#32828B'
    : props.theme?.colors?.border?.secondary || 'rgba(255, 255, 255, 0.1)'};
  border-radius: ${borderRadius.sm};
  cursor: ${props => props.$disabled ? 'not-allowed' : 'pointer'};
  opacity: ${props => props.$disabled ? 0.5 : 1};
  transition: ${transitions.all};

  &:hover {
    background: ${props => !props.$disabled && (props.$checked
      ? `${props.theme?.colors?.accent?.primary || '#32828B'}30`
      : props.theme?.colors?.background?.hover || 'rgba(255, 255, 255, 0.05)')};
  }

  input {
    display: none;
  }
`;

const RoleBadge = styled.span<{ $role: string }>`
  display: inline-flex;
  align-items: center;
  padding: 2px 6px;
  font-size: 10px;
  font-weight: ${typography.fontWeight.medium};
  color: ${props => {
    switch (props.$role) {
      case 'admin': return '#ef4444';
      case 'analyst': return '#3b82f6';
      case 'viewer': return '#22c55e';
      case 'editor': return '#f59e0b';
      default: return '#94a3b8';
    }
  }};
  background: ${props => {
    switch (props.$role) {
      case 'admin': return '#ef444420';
      case 'analyst': return '#3b82f620';
      case 'viewer': return '#22c55e20';
      case 'editor': return '#f59e0b20';
      default: return '#94a3b820';
    }
  }};
  border-radius: ${borderRadius.sm};
  text-transform: capitalize;
`;

const SubItemsContainer = styled.div`
  padding: ${spacing['2']} ${spacing['3']};
  padding-left: ${spacing['10']};
  background: ${props => props.theme?.colors?.background?.tertiary || 'rgba(20, 20, 20, 0.5)'};
  border-top: 1px solid ${props => props.theme?.colors?.border?.secondary || 'rgba(255, 255, 255, 0.05)'};
`;

const SubItemRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: ${spacing['1']} 0;
  font-size: ${typography.fontSize.xs};
  color: ${props => props.theme?.colors?.text?.secondary || '#94a3b8'};

  &:not(:last-child) {
    border-bottom: 1px dashed ${props => props.theme?.colors?.border?.secondary || 'rgba(255, 255, 255, 0.05)'};
  }
`;

const ArabicLabel = styled.span`
  font-size: ${typography.fontSize.xs};
  color: ${props => props.theme?.colors?.text?.tertiary || '#64748b'};
  margin-left: ${spacing['2']};
  direction: rtl;
`;

// Number input for session timeout
const NumberInput = styled.input`
  width: 80px;
  padding: ${spacing['2']};
  font-size: ${typography.fontSize.sm};
  color: ${props => props.theme?.colors?.text?.primary || '#ffffff'};
  background: ${props => props.theme?.colors?.background?.secondary || 'rgba(26, 26, 26, 0.5)'};
  border: 1px solid ${props => props.theme?.colors?.border?.primary || 'rgba(255, 255, 255, 0.1)'};
  border-radius: ${borderRadius.md};
  text-align: center;
  transition: ${transitions.all};

  &:focus {
    outline: none;
    border-color: ${props => props.theme?.colors?.accent?.primary || '#32828B'};
    box-shadow: 0 0 0 2px ${props => `${props.theme?.colors?.accent?.primary || '#32828B'}20`};
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }

  /* Hide spinner buttons */
  -moz-appearance: textfield;
  &::-webkit-outer-spin-button,
  &::-webkit-inner-spin-button {
    -webkit-appearance: none;
    margin: 0;
  }
`;

const NumberInputWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: ${spacing['2']};
`;

const NumberInputLabel = styled.span`
  font-size: ${typography.fontSize.xs};
  color: ${props => props.theme?.colors?.text?.secondary || '#94a3b8'};
`;

// Role Access Matrix styles
const RoleMatrixTable = styled.table`
  width: 100%;
  border-collapse: collapse;
  font-size: ${typography.fontSize.sm};
`;

const RoleMatrixHeader = styled.th`
  padding: ${spacing['2']} ${spacing['3']};
  text-align: left;
  font-weight: ${typography.fontWeight.semibold};
  color: ${props => props.theme?.colors?.text?.primary || '#ffffff'};
  background: ${props => props.theme?.colors?.background?.tertiary || 'rgba(20, 20, 20, 0.5)'};
  border-bottom: 2px solid ${props => props.theme?.colors?.border?.primary || 'rgba(255, 255, 255, 0.1)'};

  &:first-child {
    border-radius: ${borderRadius.md} 0 0 0;
  }

  &:last-child {
    border-radius: 0 ${borderRadius.md} 0 0;
  }
`;

const RoleMatrixRow = styled.tr`
  &:hover {
    background: ${props => props.theme?.colors?.background?.hover || 'rgba(255, 255, 255, 0.05)'};
  }
`;

const RoleMatrixCell = styled.td`
  padding: ${spacing['2']} ${spacing['3']};
  border-bottom: 1px solid ${props => props.theme?.colors?.border?.secondary || 'rgba(255, 255, 255, 0.05)'};
  color: ${props => props.theme?.colors?.text?.primary || '#ffffff'};

  &:first-child {
    font-weight: ${typography.fontWeight.medium};
  }
`;

const RoleMatrixCheckboxCell = styled.td`
  padding: ${spacing['2']} ${spacing['3']};
  border-bottom: 1px solid ${props => props.theme?.colors?.border?.secondary || 'rgba(255, 255, 255, 0.05)'};
  text-align: center;
`;

const RoleMatrixCheckbox = styled.input`
  width: 18px;
  height: 18px;
  cursor: pointer;
  accent-color: ${props => props.theme?.colors?.accent?.primary || '#32828B'};

  &:disabled {
    cursor: not-allowed;
    opacity: 0.5;
  }
`;

const RoleHeaderCell = styled.th<{ $role: string }>`
  padding: ${spacing['2']} ${spacing['3']};
  text-align: center;
  font-weight: ${typography.fontWeight.semibold};
  color: ${props => {
    switch (props.$role) {
      case 'admin': return '#ef4444';
      case 'analyst': return '#3b82f6';
      case 'viewer': return '#22c55e';
      case 'editor': return '#f59e0b';
      default: return '#ffffff';
    }
  }};
  background: ${props => {
    switch (props.$role) {
      case 'admin': return '#ef444410';
      case 'analyst': return '#3b82f610';
      case 'viewer': return '#22c55e10';
      case 'editor': return '#f59e0b10';
      default: return 'rgba(20, 20, 20, 0.5)';
    }
  }};
  border-bottom: 2px solid ${props => props.theme?.colors?.border?.primary || 'rgba(255, 255, 255, 0.1)'};
  text-transform: capitalize;
`;

// =============================================================================
// ROLE SELECTOR COMPONENT
// =============================================================================

interface RoleSelectorProps {
  roles: UserRole[];
  onChange: (role: UserRole, enabled: boolean) => void;
  disabled?: boolean;
}

const RoleSelector: React.FC<RoleSelectorProps> = ({ roles, onChange, disabled }) => (
  <RolesContainer style={{ marginTop: '4px', marginLeft: 0 }}>
    {USER_ROLES.map(({ id, label }) => {
      const isChecked = roles.includes(id);
      return (
        <RoleCheckbox
          key={id}
          $checked={isChecked}
          $disabled={disabled}
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            if (!disabled) onChange(id, !isChecked);
          }}
        >
          <input type="checkbox" checked={isChecked} readOnly />
          {label}
        </RoleCheckbox>
      );
    })}
  </RolesContainer>
);

// =============================================================================
// COMPONENT
// =============================================================================

export const FeatureFlagsEditor: React.FC<FeatureFlagsEditorProps> = ({
  projectId,
  featureFlags,
  backendFeatureFlags,
  frontendFeatureFlags,
  settingsConfig,
  dataSourcesConfig,
  mapStylesConfig,
  localizationConfig,
  themeConfig,
  globalFeaturesConfig,
  backendConfig,
  onChange,
  onSave,
  onReset,
  loading = false,
  readOnly = false,
}) => {
  // Section expansion state
  const [expandedSections, setExpandedSections] = useState<Set<string>>(
    new Set(['roleMatrix', 'modules', 'manifest', 'backend', 'frontend', 'global'])
  );
  const [expandedModules, setExpandedModules] = useState<Set<string>>(new Set());

  // The tenant's own manifest, fetched from the GUS deployment via the portal.
  // Null until loaded (or if there is no projectId, e.g. create mode).
  const [manifest, setManifest] = useState<FlagManifestResponse | null>(null);
  // Why the fetch failed, if it did. `manifest === null` alone renders neither
  // the tree nor the "not published yet" alert, so a 404, a 500 and an auth
  // failure all looked exactly like "no project selected" -- which is how the
  // endpoint being unreachable for this entire feature went unnoticed.
  const [manifestError, setManifestError] = useState<string | null>(null);

  useEffect(() => {
    if (!projectId) return;
    setManifestError(null);
    projectAPI
      .getFlagManifest(projectId)
      .then((response) => {
        setManifest(response);
        setManifestError(null);
      })
      .catch((error: any) => {
        setManifest(null);
        const status = error?.response?.status;
        const detail = error?.response?.data?.detail;
        setManifestError(
          status
            ? `HTTP ${status}${detail ? `: ${detail}` : ''}`
            : error?.message || 'the request did not complete'
        );
      });
  }, [projectId]);

  // Check if data is available
  const hasNoData = !featureFlags && !backendFeatureFlags && !frontendFeatureFlags;

  // Initialize with defaults if no data - memoized to avoid re-renders
  const modules = useMemo(() => featureFlags?.modules || {}, [featureFlags]);
  // The manifest tree's root. Manifest paths are resolved against the whole
  // `feature_flags` object, not against `feature_flags.modules` -- GUS does
  // `getValueByPath(config.features, path)` where `config.features` IS
  // `feature_flags`, and `utilities.settings.*` lives outside `modules`.
  // It is also live state, which is why the manifest response no longer
  // carries the project's stored flags at all: reading that snapshot (fetched
  // once) while writing into `featureFlags` meant a control never moved when
  // it was clicked.
  const storedFlagRoot = useMemo<Record<string, any>>(
    () => (featureFlags as Record<string, any>) || {},
    [featureFlags]
  );
  const backendFlags = useMemo(() => backendFeatureFlags || {}, [backendFeatureFlags]);
  const frontendFlags = useMemo(() => frontendFeatureFlags || {}, [frontendFeatureFlags]);
  const settings = useMemo(() => settingsConfig || {}, [settingsConfig]);
  const dataSources = useMemo(() => dataSourcesConfig || {}, [dataSourcesConfig]);
  const mapStyles = useMemo(() => mapStylesConfig || {}, [mapStylesConfig]);
  const globalFeatures = useMemo(() => globalFeaturesConfig || {}, [globalFeaturesConfig]);

  // Toggle section expansion
  const toggleSection = useCallback((sectionId: string) => {
    setExpandedSections((prev) => {
      const next = new Set(prev);
      if (next.has(sectionId)) {
        next.delete(sectionId);
      } else {
        next.add(sectionId);
      }
      return next;
    });
  }, []);

  // Toggle module expansion
  const toggleModule = useCallback((moduleId: string) => {
    setExpandedModules((prev) => {
      const next = new Set(prev);
      if (next.has(moduleId)) {
        next.delete(moduleId);
      } else {
        next.add(moduleId);
      }
      return next;
    });
  }, []);

  // Handle module toggle
  const handleModuleToggle = useCallback(
    (moduleId: string, enabled: boolean) => {
      const newModules = {
        ...modules,
        [moduleId]: {
          ...modules[moduleId],
          enabled,
        },
      };
      onChange({ featureFlags: { ...storedFlagRoot, modules: newModules } });
    },
    [modules, onChange]
  );

  // Handle tab toggle. `container` is the key the module's tabs are saved under (tabsContainer).
  const handleTabToggle = useCallback(
    (moduleId: string, tabId: string, enabled: boolean, container: 'tabs' | 'submenu' = 'tabs') => {
      const module = modules[moduleId] || { enabled: true };
      const items = module[container] || {};
      const newModules = {
        ...modules,
        [moduleId]: {
          ...module,
          [container]: {
            ...items,
            [tabId]: {
              ...(items[tabId] || {}),
              enabled,
            },
          },
        },
      };
      onChange({ featureFlags: { ...storedFlagRoot, modules: newModules } });
    },
    [modules, onChange]
  );

  // Write a dot path from the manifest tree into the whole `feature_flags`
  // object without disturbing any sibling the manifest (or this component)
  // doesn't know about. The actual splice-and-copy logic lives in flagPaths.ts,
  // pinned by its own tests.
  //
  // It emits the WHOLE updated object, not `{ modules: next }`. ProjectEditor
  // does `setFeatureFlags(updates.featureFlags)` -- a replace, not a merge --
  // and then saves `feature_flags` wholesale, so emitting only `modules` erased
  // every top-level sibling of it (`utilities` included) on any flag edit.
  const setFlagPath = useCallback(
    (path: string, value: any) => {
      const next = setPath(storedFlagRoot, path, value);
      onChange({ featureFlags: next as FeatureFlagsConfig });
    },
    [storedFlagRoot, onChange]
  );

  // City Satisfaction's additions to the tree: where each old switch now lives,
  // and the editors for the settings the manifest does not describe. The
  // Leadership Brief adds its own notes to its node; both decorations merge.
  const decorateTree = useMemo<Decorate | undefined>(() => {
    if (!manifest?.manifest) return undefined;
    const sat = satisfactionDecorate(manifest.manifest, storedFlagRoot, setFlagPath, readOnly);
    const exec = executiveDecorate(manifest.manifest, storedFlagRoot);
    return (node) => {
      const a = sat(node);
      const b = exec(node);
      if (!a || !b) return a ?? b;
      return {
        badges: [...(a.badges ?? []), ...(b.badges ?? [])],
        notices: <>{a.notices}{b.notices}</>,
        body: <>{a.body}{b.body}</>,
        declaredKeys: [...(a.declaredKeys ?? []), ...(b.declaredKeys ?? [])],
      };
    };
  }, [manifest, storedFlagRoot, setFlagPath, readOnly]);

  // Handle tool toggle (for Map workspace)
  const handleToolToggle = useCallback(
    (moduleId: string, toolId: string, enabled: boolean) => {
      const module = modules[moduleId] || { enabled: true, tools: {} };
      const newModules = {
        ...modules,
        [moduleId]: {
          ...module,
          tools: {
            ...module.tools,
            [toolId]: {
              ...(module.tools?.[toolId] || {}),
              enabled,
            },
          },
        },
      };
      onChange({ featureFlags: { ...storedFlagRoot, modules: newModules } });
    },
    [modules, onChange]
  );

  // Handle feature toggle (for Data Management, Maps Library)
  const handleFeatureToggle = useCallback(
    (moduleId: string, featureId: string, enabled: boolean) => {
      const module = modules[moduleId] || { enabled: true, features: {} };
      const newModules = {
        ...modules,
        [moduleId]: {
          ...module,
          features: {
            ...module.features,
            [featureId]: {
              ...(module.features?.[featureId] || {}),
              enabled,
            },
          },
        },
      };
      onChange({ featureFlags: { ...storedFlagRoot, modules: newModules } });
    },
    [modules, onChange]
  );

  // Handle widget toggle (for Dashboard)
  const handleWidgetToggle = useCallback(
    (moduleId: string, widgetId: string, enabled: boolean) => {
      const module = modules[moduleId] || { enabled: true, widgets: {} };
      const newModules = {
        ...modules,
        [moduleId]: {
          ...module,
          widgets: {
            ...module.widgets,
            [widgetId]: {
              ...(module.widgets?.[widgetId] || {}),
              enabled,
            },
          },
        },
      };
      onChange({ featureFlags: { ...storedFlagRoot, modules: newModules } });
    },
    [modules, onChange]
  );

  // Handle module role toggle
  const handleModuleRoleToggle = useCallback(
    (moduleId: string, role: UserRole, enabled: boolean) => {
      const module = modules[moduleId] || { enabled: true, roles: [] };
      const currentRoles = module.roles || [];
      const newRoles = enabled
        ? [...currentRoles, role]
        : currentRoles.filter(r => r !== role);
      const newModules = {
        ...modules,
        [moduleId]: {
          ...module,
          roles: newRoles,
        },
      };
      onChange({ featureFlags: { ...storedFlagRoot, modules: newModules } });
    },
    [modules, onChange]
  );

  // Handle item role toggle (tabs, submenu, tools, features, widgets)
  const handleItemRoleToggle = useCallback(
    (moduleId: string, itemType: 'tabs' | 'submenu' | 'tools' | 'features' | 'widgets', itemId: string, role: UserRole, enabled: boolean) => {
      const module = modules[moduleId] || { enabled: true };
      const items = module[itemType] || {};
      const item = items[itemId] || { enabled: true, roles: module.roles || [] };
      const currentRoles = item.roles || [];
      const newRoles = enabled
        ? [...currentRoles, role]
        : currentRoles.filter(r => r !== role);
      const newModules = {
        ...modules,
        [moduleId]: {
          ...module,
          [itemType]: {
            ...items,
            [itemId]: {
              ...item,
              roles: newRoles,
            },
          },
        },
      };
      onChange({ featureFlags: { ...storedFlagRoot, modules: newModules } });
    },
    [modules, onChange]
  );

  // Handle export format toggle
  const handleExportFormatToggle = useCallback(
    (moduleId: string, formatId: string, enabled: boolean) => {
      const module = modules[moduleId] || { enabled: true, export_formats: {} };
      const newModules = {
        ...modules,
        [moduleId]: {
          ...module,
          export_formats: {
            ...module.export_formats,
            [formatId]: { enabled },
          },
        },
      };
      onChange({ featureFlags: { ...storedFlagRoot, modules: newModules } });
    },
    [modules, onChange]
  );

  // Handle backend flag toggle
  const handleBackendFlagToggle = useCallback(
    (flagId: string, enabled: boolean) => {
      onChange({
        backendFeatureFlags: {
          ...backendFlags,
          [flagId]: enabled,
        },
      });
    },
    [backendFlags, onChange]
  );

  // Handle frontend flag toggle
  const handleFrontendFlagToggle = useCallback(
    (flagId: string, enabled: boolean) => {
      onChange({
        frontendFeatureFlags: {
          ...frontendFlags,
          [flagId]: enabled,
        },
      });
    },
    [frontendFlags, onChange]
  );

  // Handle settings toggle
  const handleSettingsToggle = useCallback(
    (tabId: string, enabled: boolean) => {
      onChange({
        settingsConfig: {
          ...settings,
          [tabId]: {
            ...settings[tabId],
            enabled,
          },
        },
      });
    },
    [settings, onChange]
  );

  // Handle data source toggle
  const handleDataSourceToggle = useCallback(
    (sourceId: string, enabled: boolean) => {
      onChange({
        dataSourcesConfig: {
          ...dataSources,
          [sourceId]: {
            ...dataSources[sourceId],
            enabled,
          },
        },
      });
    },
    [dataSources, onChange]
  );

  // Handle map style toggle
  const handleMapStyleToggle = useCallback(
    (styleId: string, enabled: boolean) => {
      onChange({
        mapStylesConfig: {
          ...mapStyles,
          [styleId]: {
            ...mapStyles[styleId],
            enabled,
          },
        },
      });
    },
    [mapStyles, onChange]
  );

  // Handle global feature toggle
  const handleGlobalFeatureToggle = useCallback(
    (featureId: string, enabled: boolean) => {
      onChange({
        globalFeaturesConfig: {
          ...globalFeatures,
          [featureId]: {
            ...globalFeatures[featureId],
            enabled,
          },
        },
      });
    },
    [globalFeatures, onChange]
  );

  // Handle global feature role toggle
  const handleGlobalFeatureRoleToggle = useCallback(
    (featureId: string, role: UserRole, enabled: boolean) => {
      const feature = globalFeatures[featureId] || { enabled: true, roles: [] };
      const currentRoles = feature.roles || [];
      const newRoles = enabled
        ? [...currentRoles, role]
        : currentRoles.filter(r => r !== role);
      onChange({
        globalFeaturesConfig: {
          ...globalFeatures,
          [featureId]: {
            ...feature,
            roles: newRoles,
          },
        },
      });
    },
    [globalFeatures, onChange]
  );

  // Handle backend config toggle
  const handleBackendConfigToggle = useCallback(
    (key: keyof BackendConfig, value: boolean | number) => {
      onChange({
        backendConfig: {
          ...backendConfig,
          [key]: value,
        } as BackendConfig,
      });
    },
    [backendConfig, onChange]
  );

  // Enable/disable all in a section
  const enableAllInSection = useCallback(
    (section: string, enable: boolean) => {
      switch (section) {
        case 'modules':
          const newModules: FeatureFlagsConfig['modules'] = {};
          MODULE_DEFINITIONS.forEach((mod) => {
            newModules[mod.id] = { ...modules[mod.id], enabled: enable };
          });
          onChange({ featureFlags: { ...storedFlagRoot, modules: newModules } });
          break;
        case 'backend':
          const newBackend: BackendFeatureFlags = {};
          BACKEND_FLAGS.forEach((flag) => {
            newBackend[flag.id] = enable;
          });
          onChange({ backendFeatureFlags: newBackend });
          break;
        case 'frontend':
          const newFrontend: FrontendFeatureFlags = {};
          FRONTEND_FLAGS.forEach((flag) => {
            newFrontend[flag.id] = enable;
          });
          onChange({ frontendFeatureFlags: newFrontend });
          break;
        case 'settings':
          const newSettings: SettingsConfig = {};
          SETTINGS_TABS.forEach((tab) => {
            newSettings[tab.id] = { ...settings[tab.id], enabled: enable };
          });
          onChange({ settingsConfig: newSettings });
          break;
        case 'dataSources':
          const newDataSources: DataSourcesConfig = {};
          DATA_SOURCES.forEach((src) => {
            newDataSources[src.id] = { ...dataSources[src.id], enabled: enable };
          });
          onChange({ dataSourcesConfig: newDataSources });
          break;
        case 'mapStyles':
          const newMapStyles: MapStylesConfig = {};
          MAP_STYLES.forEach((style) => {
            newMapStyles[style.id] = { ...mapStyles[style.id], enabled: enable };
          });
          onChange({ mapStylesConfig: newMapStyles });
          break;
        case 'global':
          const newGlobal: GlobalFeaturesConfig = {};
          GLOBAL_FEATURES.forEach((feature) => {
            newGlobal[feature.id] = { enabled: enable };
          });
          onChange({ globalFeaturesConfig: newGlobal });
          break;
      }
    },
    [modules, settings, dataSources, mapStyles, onChange]
  );

  // Count enabled items in each section
  const counts = useMemo(() => ({
    modules: MODULE_DEFINITIONS.filter((m) => modules[m.id]?.enabled).length,
    backend: BACKEND_FLAGS.filter((f) => backendFlags[f.id]).length,
    frontend: FRONTEND_FLAGS.filter((f) => frontendFlags[f.id]).length,
    settings: SETTINGS_TABS.filter((t) => settings[t.id]?.enabled).length,
    dataSources: DATA_SOURCES.filter((s) => dataSources[s.id]?.enabled).length,
    mapStyles: MAP_STYLES.filter((s) => mapStyles[s.id]?.enabled).length,
    global: GLOBAL_FEATURES.filter((f) => globalFeatures[f.id]?.enabled).length,
  }), [modules, backendFlags, frontendFlags, settings, dataSources, mapStyles, globalFeatures]);

  // Load defaults for empty config
  const loadDefaults = useCallback(() => {
    const defaultModules: FeatureFlagsConfig['modules'] = {};
    MODULE_DEFINITIONS.forEach((mod) => {
      defaultModules[mod.id] = {
        enabled: true,
        roles: mod.roles || [],
      };

      // Handle tabs (for Atlas, Reports, Assessments, GUS Chat)
      if (mod.tabs && mod.tabs.length > 0) {
        const tabsKey = mod.tabsContainer ?? 'tabs';
        defaultModules[mod.id][tabsKey] = {};
        mod.tabs.forEach((tab) => {
          defaultModules[mod.id][tabsKey]![tab.id] = {
            enabled: tab.defaultEnabled ?? true,
            roles: tab.roles || mod.roles || [],
          };
        });
      }

      // Handle tools (for Map Workspace)
      if (mod.tools && mod.tools.length > 0) {
        defaultModules[mod.id].tools = {};
        mod.tools.forEach((tool) => {
          defaultModules[mod.id].tools![tool.id] = {
            enabled: true,
            roles: tool.roles || mod.roles || [],
          };
        });
      }

      // Handle features (for Data Management, Maps Library)
      if (mod.features && mod.features.length > 0) {
        defaultModules[mod.id].features = {};
        mod.features.forEach((feature) => {
          defaultModules[mod.id].features![feature.id] = {
            enabled: true,
            roles: feature.roles || mod.roles || [],
          };
        });
      }

      // Handle widgets (for Dashboard)
      if (mod.widgets && mod.widgets.length > 0) {
        defaultModules[mod.id].widgets = {};
        mod.widgets.forEach((widget) => {
          defaultModules[mod.id].widgets![widget.id] = {
            enabled: true,
            roles: widget.roles || mod.roles || [],
          };
        });
      }

      // Handle export formats (for Map Workspace)
      if (mod.export_formats && mod.export_formats.length > 0) {
        defaultModules[mod.id].export_formats = {};
        mod.export_formats.forEach((format) => {
          defaultModules[mod.id].export_formats![format.id] = { enabled: true };
        });
      }
    });

    const defaultBackend: BackendFeatureFlags = {};
    BACKEND_FLAGS.forEach((f) => { defaultBackend[f.id] = true; });

    const defaultFrontend: FrontendFeatureFlags = {};
    FRONTEND_FLAGS.forEach((f) => { defaultFrontend[f.id] = true; });

    const defaultSettings: SettingsConfig = {};
    SETTINGS_TABS.forEach((t) => { defaultSettings[t.id] = { enabled: true }; });

    const defaultDataSources: DataSourcesConfig = {};
    DATA_SOURCES.forEach((s) => { defaultDataSources[s.id] = { enabled: true }; });

    const defaultMapStyles: MapStylesConfig = {};
    MAP_STYLES.forEach((s) => { defaultMapStyles[s.id] = { enabled: true }; });

    const defaultGlobalFeatures: GlobalFeaturesConfig = {};
    GLOBAL_FEATURES.forEach((f) => { defaultGlobalFeatures[f.id] = { enabled: true }; });

    onChange({
      featureFlags: { modules: defaultModules },
      backendFeatureFlags: defaultBackend,
      frontendFeatureFlags: defaultFrontend,
      settingsConfig: defaultSettings,
      dataSourcesConfig: defaultDataSources,
      mapStylesConfig: defaultMapStyles,
      globalFeaturesConfig: defaultGlobalFeatures,
    });
  }, [onChange]);

  return (
    <EditorContainer>
      {/* Warning for no data */}
      {hasNoData && (
        <Alert
          severity="warning"
          icon={<WarningIcon />}
          action={
            <MuiButton color="inherit" size="small" onClick={loadDefaults}>
              Load Defaults
            </MuiButton>
          }
        >
          Feature flags not configured. Click "Load Defaults" to initialize with all features enabled.
        </Alert>
      )}

      {/* Role Access Defaults Section */}
      <Section variant="outlined">
        <SectionHeader
          $expanded={expandedSections.has('roleMatrix')}
          onClick={() => toggleSection('roleMatrix')}
        >
          <SectionTitle>
            <SectionTitleText>Role Access Defaults</SectionTitleText>
            <SectionSubtitle>Quick access matrix to set which roles can access each module</SectionSubtitle>
          </SectionTitle>
          <SectionActions>
            {expandedSections.has('roleMatrix') ? <ExpandLess /> : <ExpandMore />}
          </SectionActions>
        </SectionHeader>
        <Collapse in={expandedSections.has('roleMatrix')}>
          <SectionContent>
            <RoleMatrixTable>
              <thead>
                <tr>
                  <RoleMatrixHeader>Module</RoleMatrixHeader>
                  {USER_ROLES.map(({ id, label }) => (
                    <RoleHeaderCell key={id} $role={id}>
                      {label}
                    </RoleHeaderCell>
                  ))}
                </tr>
              </thead>
              <tbody>
                {MODULE_DEFINITIONS.map((mod) => {
                  const moduleRoles = modules[mod.id]?.roles || mod.roles || [];
                  return (
                    <RoleMatrixRow key={mod.id}>
                      <RoleMatrixCell>
                        {mod.label}
                        {mod.label_ar && <ArabicLabel>{mod.label_ar}</ArabicLabel>}
                      </RoleMatrixCell>
                      {USER_ROLES.map(({ id }) => {
                        const isChecked = moduleRoles.includes(id);
                        return (
                          <RoleMatrixCheckboxCell key={id}>
                            <RoleMatrixCheckbox
                              type="checkbox"
                              checked={isChecked}
                              disabled={readOnly || !modules[mod.id]?.enabled}
                              onChange={(e) => handleModuleRoleToggle(mod.id, id, e.target.checked)}
                            />
                          </RoleMatrixCheckboxCell>
                        );
                      })}
                    </RoleMatrixRow>
                  );
                })}
              </tbody>
            </RoleMatrixTable>
          </SectionContent>
        </Collapse>
      </Section>

      {/* Modules Section */}
      <Section variant="outlined">
        <SectionHeader
          $expanded={expandedSections.has('modules')}
          onClick={() => toggleSection('modules')}
        >
          <SectionTitle>
            <SectionTitleText>
              Modules <CountBadge>{counts.modules}/{MODULE_DEFINITIONS.length}</CountBadge>
            </SectionTitleText>
            <SectionSubtitle>Configure which GUS modules are enabled</SectionSubtitle>
          </SectionTitle>
          <SectionActions>
            {!readOnly && (
              <>
                <EnableAllButton onClick={(e) => { e.stopPropagation(); enableAllInSection('modules', true); }}>
                  Enable All
                </EnableAllButton>
                <EnableAllButton onClick={(e) => { e.stopPropagation(); enableAllInSection('modules', false); }}>
                  Disable All
                </EnableAllButton>
              </>
            )}
            {expandedSections.has('modules') ? <ExpandLess /> : <ExpandMore />}
          </SectionActions>
        </SectionHeader>
        <Collapse in={expandedSections.has('modules')}>
          <SectionContent>
            {MODULE_DEFINITIONS.map((mod) => {
              const hasItems = (mod.tabs && mod.tabs.length > 0) ||
                (mod.tools && mod.tools.length > 0) ||
                (mod.features && mod.features.length > 0) ||
                (mod.widgets && mod.widgets.length > 0) ||
                (mod.export_formats && mod.export_formats.length > 0);
              const tabsKey = mod.tabsContainer ?? 'tabs';

              return (
                <ModuleItem key={mod.id}>
                  <ModuleHeader
                    $enabled={modules[mod.id]?.enabled || false}
                    onClick={() => hasItems && toggleModule(mod.id)}
                  >
                    <ModuleInfo>
                      <ModuleName>
                        {mod.label}
                        {mod.label_ar && <ArabicLabel>{mod.label_ar}</ArabicLabel>}
                      </ModuleName>
                      <ModuleDescription>
                        {mod.description}
                        {!readOnly ? (
                          <RoleSelector
                            roles={modules[mod.id]?.roles || mod.roles || []}
                            onChange={(role, enabled) => handleModuleRoleToggle(mod.id, role, enabled)}
                            disabled={!modules[mod.id]?.enabled}
                          />
                        ) : (
                          (modules[mod.id]?.roles || mod.roles || []).length > 0 && (
                            <RolesContainer style={{ marginTop: '4px', marginLeft: 0 }}>
                              {(modules[mod.id]?.roles || mod.roles || []).map(role => (
                                <RoleBadge key={role} $role={role}>{role}</RoleBadge>
                              ))}
                            </RolesContainer>
                          )
                        )}
                      </ModuleDescription>
                    </ModuleInfo>
                    <SectionActions>
                      <Toggle
                        checked={modules[mod.id]?.enabled || false}
                        onChange={(checked) => handleModuleToggle(mod.id, checked)}
                        disabled={readOnly}
                        size="sm"
                      />
                      {hasItems && (
                        expandedModules.has(mod.id) ? <ExpandLess /> : <ExpandMore />
                      )}
                    </SectionActions>
                  </ModuleHeader>

                  {/* Tabs (for Atlas, Reports, Assessments, GUS Chat) */}
                  {mod.tabs && mod.tabs.length > 0 && (
                    <Collapse in={expandedModules.has(mod.id)}>
                      <TabsContainer>
                        {mod.tabs.map((tab) => (
                          <div key={tab.id}>
                            <TabItem>
                              <div>
                                <span>{tab.label}</span>
                                {tab.label_ar && <ArabicLabel>{tab.label_ar}</ArabicLabel>}
                                {!readOnly ? (
                                  <RoleSelector
                                    roles={modules[mod.id]?.[tabsKey]?.[tab.id]?.roles || tab.roles || modules[mod.id]?.roles || mod.roles || []}
                                    onChange={(role, enabled) => handleItemRoleToggle(mod.id, tabsKey, tab.id, role, enabled)}
                                    disabled={!modules[mod.id]?.enabled || modules[mod.id]?.[tabsKey]?.[tab.id]?.enabled === false}
                                  />
                                ) : (
                                  (modules[mod.id]?.[tabsKey]?.[tab.id]?.roles || tab.roles || modules[mod.id]?.roles || mod.roles || []).length > 0 && (
                                    <RolesContainer>
                                      {(modules[mod.id]?.[tabsKey]?.[tab.id]?.roles || tab.roles || modules[mod.id]?.roles || mod.roles || []).map(role => (
                                        <RoleBadge key={role} $role={role}>{role}</RoleBadge>
                                      ))}
                                    </RolesContainer>
                                  )
                                )}
                              </div>
                              <Toggle
                                checked={tabIsOn(modules[mod.id]?.[tabsKey]?.[tab.id], tab.defaultEnabled ?? true)}
                                onChange={(checked) => handleTabToggle(mod.id, tab.id, checked, tabsKey)}
                                disabled={readOnly || !modules[mod.id]?.enabled}
                                size="sm"
                              />
                            </TabItem>
                            {/* Subtabs */}
                            {tab.subtabs && tab.subtabs.length > 0 && modules[mod.id]?.[tabsKey]?.[tab.id]?.enabled !== false && (
                              <SubItemsContainer>
                                {tab.subtabs.map(subtab => (
                                  <SubItemRow key={subtab.id}>
                                    <span>{subtab.label}</span>
                                  </SubItemRow>
                                ))}
                              </SubItemsContainer>
                            )}
                            {/* Report Generator Stages */}
                            {tab.stages && tab.stages.length > 0 && modules[mod.id]?.[tabsKey]?.[tab.id]?.enabled !== false && (
                              <SubItemsContainer>
                                <SubItemRow style={{ fontWeight: 500, color: '#94a3b8' }}>Stages</SubItemRow>
                                {tab.stages.map(stage => (
                                  <SubItemRow key={stage.id}>
                                    <span>{stage.label}</span>
                                  </SubItemRow>
                                ))}
                              </SubItemsContainer>
                            )}
                            {/* Report Types */}
                            {tab.report_types && tab.report_types.length > 0 && modules[mod.id]?.[tabsKey]?.[tab.id]?.enabled !== false && (
                              <SubItemsContainer>
                                <SubItemRow style={{ fontWeight: 500, color: '#94a3b8' }}>Report Types</SubItemRow>
                                {tab.report_types.map(type => (
                                  <SubItemRow key={type.id}>
                                    <span>{type.label}</span>
                                  </SubItemRow>
                                ))}
                              </SubItemsContainer>
                            )}
                          </div>
                        ))}
                      </TabsContainer>
                    </Collapse>
                  )}

                  {/* Tools (for Map Workspace) */}
                  {mod.tools && mod.tools.length > 0 && (
                    <Collapse in={expandedModules.has(mod.id)}>
                      <TabsContainer>
                        {mod.tools.map((tool) => (
                          <TabItem key={tool.id}>
                            <div>
                              <span>{tool.label}</span>
                              {tool.label_ar && <ArabicLabel>{tool.label_ar}</ArabicLabel>}
                              {!readOnly ? (
                                <RoleSelector
                                  roles={modules[mod.id]?.tools?.[tool.id]?.roles || tool.roles || modules[mod.id]?.roles || mod.roles || []}
                                  onChange={(role, enabled) => handleItemRoleToggle(mod.id, 'tools', tool.id, role, enabled)}
                                  disabled={!modules[mod.id]?.enabled || modules[mod.id]?.tools?.[tool.id]?.enabled === false}
                                />
                              ) : (
                                (modules[mod.id]?.tools?.[tool.id]?.roles || tool.roles || modules[mod.id]?.roles || mod.roles || []).length > 0 && (
                                  <RolesContainer>
                                    {(modules[mod.id]?.tools?.[tool.id]?.roles || tool.roles || modules[mod.id]?.roles || mod.roles || []).map(role => (
                                      <RoleBadge key={role} $role={role}>{role}</RoleBadge>
                                    ))}
                                  </RolesContainer>
                                )
                              )}
                            </div>
                            <Toggle
                              checked={modules[mod.id]?.tools?.[tool.id]?.enabled ?? true}
                              onChange={(checked) => handleToolToggle(mod.id, tool.id, checked)}
                              disabled={readOnly || !modules[mod.id]?.enabled}
                              size="sm"
                            />
                          </TabItem>
                        ))}
                        {/* Export Formats */}
                        {mod.export_formats && mod.export_formats.length > 0 && (
                          <>
                            <TabItem style={{ marginTop: '8px', borderTop: '1px solid rgba(255,255,255,0.1)', paddingTop: '8px' }}>
                              <span style={{ fontWeight: 500, color: '#94a3b8' }}>Export Formats</span>
                            </TabItem>
                            {mod.export_formats.map((format) => (
                              <TabItem key={format.id}>
                                <span>{format.label}</span>
                                <Toggle
                                  checked={modules[mod.id]?.export_formats?.[format.id]?.enabled ?? true}
                                  onChange={(checked) => handleExportFormatToggle(mod.id, format.id, checked)}
                                  disabled={readOnly || !modules[mod.id]?.enabled}
                                  size="sm"
                                />
                              </TabItem>
                            ))}
                          </>
                        )}
                      </TabsContainer>
                    </Collapse>
                  )}

                  {/* Features (for Data Management, Maps Library) */}
                  {mod.features && mod.features.length > 0 && (
                    <Collapse in={expandedModules.has(mod.id)}>
                      <TabsContainer>
                        {mod.features.map((feature) => (
                          <TabItem key={feature.id}>
                            <div>
                              <span>{feature.label}</span>
                              {!readOnly ? (
                                <RoleSelector
                                  roles={modules[mod.id]?.features?.[feature.id]?.roles || feature.roles || modules[mod.id]?.roles || mod.roles || []}
                                  onChange={(role, enabled) => handleItemRoleToggle(mod.id, 'features', feature.id, role, enabled)}
                                  disabled={!modules[mod.id]?.enabled || modules[mod.id]?.features?.[feature.id]?.enabled === false}
                                />
                              ) : (
                                (modules[mod.id]?.features?.[feature.id]?.roles || feature.roles || modules[mod.id]?.roles || mod.roles || []).length > 0 && (
                                  <RolesContainer>
                                    {(modules[mod.id]?.features?.[feature.id]?.roles || feature.roles || modules[mod.id]?.roles || mod.roles || []).map(role => (
                                      <RoleBadge key={role} $role={role}>{role}</RoleBadge>
                                    ))}
                                  </RolesContainer>
                                )
                              )}
                            </div>
                            <Toggle
                              checked={modules[mod.id]?.features?.[feature.id]?.enabled ?? true}
                              onChange={(checked) => handleFeatureToggle(mod.id, feature.id, checked)}
                              disabled={readOnly || !modules[mod.id]?.enabled}
                              size="sm"
                            />
                          </TabItem>
                        ))}
                      </TabsContainer>
                    </Collapse>
                  )}

                  {/* Widgets (for Dashboard) */}
                  {mod.widgets && mod.widgets.length > 0 && (
                    <Collapse in={expandedModules.has(mod.id)}>
                      <TabsContainer>
                        {mod.widgets.map((widget) => (
                          <TabItem key={widget.id}>
                            <div>
                              <span>{widget.label}</span>
                              {!readOnly ? (
                                <RoleSelector
                                  roles={modules[mod.id]?.widgets?.[widget.id]?.roles || widget.roles || modules[mod.id]?.roles || mod.roles || []}
                                  onChange={(role, enabled) => handleItemRoleToggle(mod.id, 'widgets', widget.id, role, enabled)}
                                  disabled={!modules[mod.id]?.enabled || modules[mod.id]?.widgets?.[widget.id]?.enabled === false}
                                />
                              ) : (
                                (modules[mod.id]?.widgets?.[widget.id]?.roles || widget.roles || modules[mod.id]?.roles || mod.roles || []).length > 0 && (
                                  <RolesContainer>
                                    {(modules[mod.id]?.widgets?.[widget.id]?.roles || widget.roles || modules[mod.id]?.roles || mod.roles || []).map(role => (
                                      <RoleBadge key={role} $role={role}>{role}</RoleBadge>
                                    ))}
                                  </RolesContainer>
                                )
                              )}
                            </div>
                            <Toggle
                              checked={modules[mod.id]?.widgets?.[widget.id]?.enabled ?? true}
                              onChange={(checked) => handleWidgetToggle(mod.id, widget.id, checked)}
                              disabled={readOnly || !modules[mod.id]?.enabled}
                              size="sm"
                            />
                          </TabItem>
                        ))}
                      </TabsContainer>
                    </Collapse>
                  )}
                </ModuleItem>
              );
            })}
          </SectionContent>
        </Collapse>
      </Section>

      {/* Manifest-driven tree, added alongside the hand-written catalogue above.
          The catalogue stays until the tree has been proven against real
          manifests and every tenant can serve one -- see task brief.

          It is a Section like every other block on this page: same card, same
          header, same disclosure, and open by default so it is exactly as
          visible as it was before it was wrapped. */}
      {/* Leadership Brief (GUS's Atlas "executive" tab): its switch and its settings. */}
      <Section variant="outlined">
        <SectionHeader
          as="button"
          type="button"
          aria-expanded={expandedSections.has('leadershipBrief')}
          aria-controls="leadership-brief-section-content"
          $expanded={expandedSections.has('leadershipBrief')}
          onClick={() => toggleSection('leadershipBrief')}
        >
          <SectionTitle>
            <SectionTitleText>
              Leadership Brief <ArabicLabel>الموجز القيادي</ArabicLabel>
            </SectionTitleText>
            <SectionSubtitle>
              Atlas tab: each person&apos;s KPIs month by month, with a map and a PowerPoint report. Off unless switched on.
            </SectionSubtitle>
          </SectionTitle>
          <SectionActions>
            {expandedSections.has('leadershipBrief') ? <ExpandLess /> : <ExpandMore />}
          </SectionActions>
        </SectionHeader>
        <Collapse in={expandedSections.has('leadershipBrief')} unmountOnExit>
          <SectionContent id="leadership-brief-section-content">
            <LeadershipBriefPanel
              projectId={projectId}
              storedFlags={storedFlagRoot}
              onChange={setFlagPath}
              disabled={readOnly}
              manifestDefaultOn={manifest?.manifest ? manifestSaysExecutiveOn(manifest.manifest) : undefined}
            />
          </SectionContent>
        </Collapse>
      </Section>

      {manifest?.manifest && (
        <Section variant="outlined">
          <SectionHeader
            as="button"
            type="button"
            aria-expanded={expandedSections.has('manifest')}
            aria-controls="manifest-section-content"
            $expanded={expandedSections.has('manifest')}
            onClick={() => toggleSection('manifest')}
          >
            <SectionTitle>
              <SectionTitleText>
                From this tenant <CountBadge>{manifest.manifest.modules.length}</CountBadge>
              </SectionTitleText>
              <SectionSubtitle>
                {manifest.gus_version ?? 'unknown version'}
                {manifest.source === 'cache' && ' — cached'}
              </SectionSubtitle>
            </SectionTitle>
            <SectionActions>
              {expandedSections.has('manifest') ? <ExpandLess /> : <ExpandMore />}
            </SectionActions>
          </SectionHeader>
          <Collapse in={expandedSections.has('manifest')}>
            <SectionContent id="manifest-section-content">
              {manifest.status !== 'ok' && (
                <Alert severity="warning" sx={{ mb: 2 }}>{manifest.detail}</Alert>
              )}
              <FlagTree
                manifest={manifest.manifest}
                storedFlags={storedFlagRoot}
                onChange={setFlagPath}
                decorate={decorateTree}
              />
            </SectionContent>
          </Collapse>
        </Section>
      )}
      {manifest && !manifest.manifest && (
        <Alert severity="info" sx={{ mt: 3 }}>
          This tenant has not published its feature flags yet ({manifest.status}
          {manifest.detail ? `: ${manifest.detail}` : ''}). The switches above are the portal's
          built-in list.
        </Alert>
      )}
      {manifestError && (
        <Alert severity="error" sx={{ mt: 3 }}>
          Could not load this tenant's flag manifest ({manifestError}). This is a portal-side
          failure, not a tenant that has published nothing — the switches above are the portal's
          built-in list, and may not match what this tenant honours.
        </Alert>
      )}

      {/* Backend Feature Flags Section */}
      <Section variant="outlined">
        <SectionHeader
          $expanded={expandedSections.has('backend')}
          onClick={() => toggleSection('backend')}
        >
          <SectionTitle>
            <SectionTitleText>
              Backend Flags <CountBadge>{counts.backend}/{BACKEND_FLAGS.length}</CountBadge>
            </SectionTitleText>
            <SectionSubtitle>Server-side feature toggles</SectionSubtitle>
          </SectionTitle>
          <SectionActions>
            {!readOnly && (
              <>
                <EnableAllButton onClick={(e) => { e.stopPropagation(); enableAllInSection('backend', true); }}>
                  Enable All
                </EnableAllButton>
                <EnableAllButton onClick={(e) => { e.stopPropagation(); enableAllInSection('backend', false); }}>
                  Disable All
                </EnableAllButton>
              </>
            )}
            {expandedSections.has('backend') ? <ExpandLess /> : <ExpandMore />}
          </SectionActions>
        </SectionHeader>
        <Collapse in={expandedSections.has('backend')}>
          <SectionContent>
            {BACKEND_FLAGS.map((flag) => (
              <FlagItem key={flag.id}>
                <FlagInfo>
                  <FlagName>{flag.label}</FlagName>
                  <FlagDescription>{flag.description}</FlagDescription>
                </FlagInfo>
                <Toggle
                  checked={backendFlags[flag.id] || false}
                  onChange={(checked) => handleBackendFlagToggle(flag.id, checked)}
                  disabled={readOnly}
                  size="sm"
                />
              </FlagItem>
            ))}
          </SectionContent>
        </Collapse>
      </Section>

      {/* Frontend Feature Flags Section */}
      <Section variant="outlined">
        <SectionHeader
          $expanded={expandedSections.has('frontend')}
          onClick={() => toggleSection('frontend')}
        >
          <SectionTitle>
            <SectionTitleText>
              Frontend Flags <CountBadge>{counts.frontend}/{FRONTEND_FLAGS.length}</CountBadge>
            </SectionTitleText>
            <SectionSubtitle>Client-side feature toggles</SectionSubtitle>
          </SectionTitle>
          <SectionActions>
            {!readOnly && (
              <>
                <EnableAllButton onClick={(e) => { e.stopPropagation(); enableAllInSection('frontend', true); }}>
                  Enable All
                </EnableAllButton>
                <EnableAllButton onClick={(e) => { e.stopPropagation(); enableAllInSection('frontend', false); }}>
                  Disable All
                </EnableAllButton>
              </>
            )}
            {expandedSections.has('frontend') ? <ExpandLess /> : <ExpandMore />}
          </SectionActions>
        </SectionHeader>
        <Collapse in={expandedSections.has('frontend')}>
          <SectionContent>
            {FRONTEND_FLAGS.map((flag) => (
              <FlagItem key={flag.id}>
                <FlagInfo>
                  <FlagName>{flag.label}</FlagName>
                  <FlagDescription>{flag.description}</FlagDescription>
                </FlagInfo>
                <Toggle
                  checked={frontendFlags[flag.id] || false}
                  onChange={(checked) => handleFrontendFlagToggle(flag.id, checked)}
                  disabled={readOnly}
                  size="sm"
                />
              </FlagItem>
            ))}
          </SectionContent>
        </Collapse>
      </Section>

      {/* Global Features Section */}
      <Section variant="outlined">
        <SectionHeader
          $expanded={expandedSections.has('global')}
          onClick={() => toggleSection('global')}
        >
          <SectionTitle>
            <SectionTitleText>
              Global Features <CountBadge>{counts.global}/{GLOBAL_FEATURES.length}</CountBadge>
            </SectionTitleText>
            <SectionSubtitle>Cross-module features used by GUS app</SectionSubtitle>
          </SectionTitle>
          <SectionActions>
            {!readOnly && (
              <>
                <EnableAllButton onClick={(e) => { e.stopPropagation(); enableAllInSection('global', true); }}>
                  Enable All
                </EnableAllButton>
                <EnableAllButton onClick={(e) => { e.stopPropagation(); enableAllInSection('global', false); }}>
                  Disable All
                </EnableAllButton>
              </>
            )}
            {expandedSections.has('global') ? <ExpandLess /> : <ExpandMore />}
          </SectionActions>
        </SectionHeader>
        <Collapse in={expandedSections.has('global')}>
          <SectionContent>
            {GLOBAL_FEATURES.map((feature) => (
              <FlagItem key={feature.id}>
                <FlagInfo>
                  <FlagName>{feature.label}</FlagName>
                  <FlagDescription>
                    {feature.description}
                    {!readOnly ? (
                      <RoleSelector
                        roles={globalFeatures[feature.id]?.roles || feature.roles || []}
                        onChange={(role, enabled) => handleGlobalFeatureRoleToggle(feature.id, role, enabled)}
                        disabled={globalFeatures[feature.id]?.enabled === false}
                      />
                    ) : (
                      (globalFeatures[feature.id]?.roles || feature.roles || []).length > 0 && (
                        <RolesContainer style={{ marginTop: '4px', marginLeft: 0 }}>
                          {(globalFeatures[feature.id]?.roles || feature.roles || []).map(role => (
                            <RoleBadge key={role} $role={role}>{role}</RoleBadge>
                          ))}
                        </RolesContainer>
                      )
                    )}
                  </FlagDescription>
                </FlagInfo>
                <Toggle
                  checked={globalFeatures[feature.id]?.enabled ?? true}
                  onChange={(checked) => handleGlobalFeatureToggle(feature.id, checked)}
                  disabled={readOnly}
                  size="sm"
                />
              </FlagItem>
            ))}
          </SectionContent>
        </Collapse>
      </Section>

      {/* Settings Tabs Section */}
      <Section variant="outlined">
        <SectionHeader
          $expanded={expandedSections.has('settings')}
          onClick={() => toggleSection('settings')}
        >
          <SectionTitle>
            <SectionTitleText>
              Settings Tabs <CountBadge>{counts.settings}/{SETTINGS_TABS.length}</CountBadge>
            </SectionTitleText>
            <SectionSubtitle>Configure which settings tabs are visible</SectionSubtitle>
          </SectionTitle>
          <SectionActions>
            {!readOnly && (
              <>
                <EnableAllButton onClick={(e) => { e.stopPropagation(); enableAllInSection('settings', true); }}>
                  Enable All
                </EnableAllButton>
                <EnableAllButton onClick={(e) => { e.stopPropagation(); enableAllInSection('settings', false); }}>
                  Disable All
                </EnableAllButton>
              </>
            )}
            {expandedSections.has('settings') ? <ExpandLess /> : <ExpandMore />}
          </SectionActions>
        </SectionHeader>
        <Collapse in={expandedSections.has('settings')}>
          <SectionContent>
            {SETTINGS_TABS.map((tab) => (
              <FlagItem key={tab.id}>
                <FlagInfo>
                  <FlagName>{tab.label}</FlagName>
                </FlagInfo>
                <Toggle
                  checked={settings[tab.id]?.enabled ?? true}
                  onChange={(checked) => handleSettingsToggle(tab.id, checked)}
                  disabled={readOnly}
                  size="sm"
                />
              </FlagItem>
            ))}
          </SectionContent>
        </Collapse>
      </Section>

      {/* Data Sources Section */}
      <Section variant="outlined">
        <SectionHeader
          $expanded={expandedSections.has('dataSources')}
          onClick={() => toggleSection('dataSources')}
        >
          <SectionTitle>
            <SectionTitleText>
              Data Sources <CountBadge>{counts.dataSources}/{DATA_SOURCES.length}</CountBadge>
            </SectionTitleText>
            <SectionSubtitle>Configure available data sources</SectionSubtitle>
          </SectionTitle>
          <SectionActions>
            {!readOnly && (
              <>
                <EnableAllButton onClick={(e) => { e.stopPropagation(); enableAllInSection('dataSources', true); }}>
                  Enable All
                </EnableAllButton>
                <EnableAllButton onClick={(e) => { e.stopPropagation(); enableAllInSection('dataSources', false); }}>
                  Disable All
                </EnableAllButton>
              </>
            )}
            {expandedSections.has('dataSources') ? <ExpandLess /> : <ExpandMore />}
          </SectionActions>
        </SectionHeader>
        <Collapse in={expandedSections.has('dataSources')}>
          <SectionContent>
            {DATA_SOURCES.map((source) => (
              <FlagItem key={source.id}>
                <FlagInfo>
                  <FlagName>{source.label}</FlagName>
                </FlagInfo>
                <Toggle
                  checked={dataSources[source.id]?.enabled ?? true}
                  onChange={(checked) => handleDataSourceToggle(source.id, checked)}
                  disabled={readOnly}
                  size="sm"
                />
              </FlagItem>
            ))}
          </SectionContent>
        </Collapse>
      </Section>

      {/* Map Styles Section */}
      <Section variant="outlined">
        <SectionHeader
          $expanded={expandedSections.has('mapStyles')}
          onClick={() => toggleSection('mapStyles')}
        >
          <SectionTitle>
            <SectionTitleText>
              Map Styles <CountBadge>{counts.mapStyles}/{MAP_STYLES.length}</CountBadge>
            </SectionTitleText>
            <SectionSubtitle>Configure available map styles</SectionSubtitle>
          </SectionTitle>
          <SectionActions>
            {!readOnly && (
              <>
                <EnableAllButton onClick={(e) => { e.stopPropagation(); enableAllInSection('mapStyles', true); }}>
                  Enable All
                </EnableAllButton>
                <EnableAllButton onClick={(e) => { e.stopPropagation(); enableAllInSection('mapStyles', false); }}>
                  Disable All
                </EnableAllButton>
              </>
            )}
            {expandedSections.has('mapStyles') ? <ExpandLess /> : <ExpandMore />}
          </SectionActions>
        </SectionHeader>
        <Collapse in={expandedSections.has('mapStyles')}>
          <SectionContent>
            {MAP_STYLES.map((style) => (
              <FlagItem key={style.id}>
                <FlagInfo>
                  <FlagName>{style.label}</FlagName>
                </FlagInfo>
                <Toggle
                  checked={mapStyles[style.id]?.enabled ?? true}
                  onChange={(checked) => handleMapStyleToggle(style.id, checked)}
                  disabled={readOnly}
                  size="sm"
                />
              </FlagItem>
            ))}
          </SectionContent>
        </Collapse>
      </Section>

      {/* Backend Configuration Section */}
      <Section variant="outlined">
        <SectionHeader
          $expanded={expandedSections.has('backendConfig')}
          onClick={() => toggleSection('backendConfig')}
        >
          <SectionTitle>
            <SectionTitleText>Backend Configuration</SectionTitleText>
            <SectionSubtitle>GUS Chat and security settings</SectionSubtitle>
          </SectionTitle>
          <SectionActions>
            {expandedSections.has('backendConfig') ? <ExpandLess /> : <ExpandMore />}
          </SectionActions>
        </SectionHeader>
        <Collapse in={expandedSections.has('backendConfig')}>
          <SectionContent>
            {/* GUS Chat Settings */}
            <ModuleItem>
              <ModuleHeader $enabled={true} style={{ cursor: 'default' }}>
                <ModuleInfo>
                  <ModuleName>GUS Chat Settings</ModuleName>
                  <ModuleDescription>Control GUS Chat behavior</ModuleDescription>
                </ModuleInfo>
              </ModuleHeader>
              <TabsContainer>
                <FlagItem>
                  <FlagInfo>
                    <FlagName>Document Only Mode</FlagName>
                    <FlagDescription>
                      Limit GUS Chat to document/RAG queries only. Enable for projects without spatial data tables.
                    </FlagDescription>
                  </FlagInfo>
                  <Toggle
                    checked={backendConfig?.document_only_mode ?? false}
                    onChange={(checked) => handleBackendConfigToggle('document_only_mode', checked)}
                    disabled={readOnly}
                    size="sm"
                  />
                </FlagItem>
                <FlagItem>
                  <FlagInfo>
                    <FlagName>LLM Query Classification</FlagName>
                    <FlagDescription>
                      Use LLM (GPT-4.1-nano) as primary query classifier. When disabled, falls back to keyword pattern matching. LLM handles typos, synonyms, and natural language better but adds ~1-3s latency.
                    </FlagDescription>
                  </FlagInfo>
                  <Toggle
                    checked={backendConfig?.enable_llm_classification ?? true}
                    onChange={(checked) => handleBackendConfigToggle('enable_llm_classification', checked)}
                    disabled={readOnly}
                    size="sm"
                  />
                </FlagItem>
                <FlagItem>
                  <FlagInfo>
                    <FlagName>Agent Dataset & Spatial Tools</FlagName>
                    <FlagDescription>
                      Expose the heavy dataset/spatial primitives (DBSCAN clustering, spatial joins, rank/aggregate, geometry pipeline) to the GUS query agent. Default OFF — the agent uses the reliable atlas searchers instead. Only enable for tenants with a genuine spatial-analysis use case (GUS #621).
                    </FlagDescription>
                  </FlagInfo>
                  <Toggle
                    checked={backendConfig?.enable_agent_dataset_tools ?? false}
                    onChange={(checked) => handleBackendConfigToggle('enable_agent_dataset_tools', checked)}
                    disabled={readOnly}
                    size="sm"
                  />
                </FlagItem>
              </TabsContainer>
            </ModuleItem>

            {/* Security Settings */}
            <ModuleItem>
              <ModuleHeader $enabled={true} style={{ cursor: 'default' }}>
                <ModuleInfo>
                  <ModuleName>Security Settings</ModuleName>
                  <ModuleDescription>Authentication and protection settings</ModuleDescription>
                </ModuleInfo>
              </ModuleHeader>
              <TabsContainer>
                <FlagItem>
                  <FlagInfo>
                    <FlagName>Enable Prompt Guard</FlagName>
                    <FlagDescription>
                      Protect against prompt injection attacks in GUS Chat
                    </FlagDescription>
                  </FlagInfo>
                  <Toggle
                    checked={backendConfig?.enable_prompt_guard ?? true}
                    onChange={(checked) => handleBackendConfigToggle('enable_prompt_guard', checked)}
                    disabled={readOnly}
                    size="sm"
                  />
                </FlagItem>
                <FlagItem>
                  <FlagInfo>
                    <FlagName>Enable Quality Metrics</FlagName>
                    <FlagDescription>
                      Score and track AI response quality
                    </FlagDescription>
                  </FlagInfo>
                  <Toggle
                    checked={backendConfig?.enable_quality_metrics ?? true}
                    onChange={(checked) => handleBackendConfigToggle('enable_quality_metrics', checked)}
                    disabled={readOnly}
                    size="sm"
                  />
                </FlagItem>
                <FlagItem>
                  <FlagInfo>
                    <FlagName>Require Email Verification</FlagName>
                    <FlagDescription>
                      Require Firebase email verification before granting access
                    </FlagDescription>
                  </FlagInfo>
                  <Toggle
                    checked={backendConfig?.require_email_verification ?? false}
                    onChange={(checked) => handleBackendConfigToggle('require_email_verification', checked)}
                    disabled={readOnly}
                    size="sm"
                  />
                </FlagItem>
                <FlagItem>
                  <FlagInfo>
                    <FlagName>Session Timeout</FlagName>
                    <FlagDescription>
                      Inactivity timeout before requiring re-authentication
                    </FlagDescription>
                  </FlagInfo>
                  <NumberInputWrapper>
                    <NumberInput
                      type="number"
                      min={1}
                      max={720}
                      value={backendConfig?.session_timeout_hours ?? 24}
                      onChange={(e) => {
                        const value = parseInt(e.target.value, 10);
                        if (!isNaN(value) && value >= 1 && value <= 720) {
                          handleBackendConfigToggle('session_timeout_hours', value);
                        }
                      }}
                      disabled={readOnly}
                    />
                    <NumberInputLabel>hours</NumberInputLabel>
                  </NumberInputWrapper>
                </FlagItem>
              </TabsContainer>
            </ModuleItem>
          </SectionContent>
        </Collapse>
      </Section>

      {/* Action Bar */}
      {(onSave || onReset) && !readOnly && (
        <ActionBar>
          {onReset && (
            <MuiButton
              variant="outlined"
              startIcon={<Undo />}
              onClick={onReset}
              disabled={loading}
            >
              Reset
            </MuiButton>
          )}
          {onSave && (
            <MuiButton
              variant="contained"
              startIcon={loading ? <CircularProgress size={18} color="inherit" /> : <Save />}
              onClick={onSave}
              disabled={loading}
              color="primary"
            >
              {loading ? 'Saving...' : 'Save Changes'}
            </MuiButton>
          )}
        </ActionBar>
      )}
    </EditorContainer>
  );
};

export default FeatureFlagsEditor;
